import { clientEnvModule } from "@/modules/client-env";
import { ThemeType } from "@/mds/themes/theme-context";
import {
  CalendarEvent,
  DesktopAudioArtifactUploadFailureDebugInfo,
  DesktopAudioArtifactRef,
  DesktopAudioArtifactSnapshot,
  DesktopRemoveAudioArtifactArgs,
  DesktopAudioRecordingRef,
  DesktopAudioUploadCompletedEvent,
  DesktopAudioUploadErrorEvent,
  DesktopAudioUploadProgressEvent,
  DesktopAudioWaveformUpdateEvent,
  DesktopMarketingAttribution,
  DesktopCriticalUpdatePromptResult,
  DesktopCriticalUpdatePromptSource,
  DesktopUpdateInstallResult,
  DesktopUpdateRestartBlocker,
  DesktopUpdateStatus,
  DesktopRecallReplaceRecordingArgs,
  DesktopRecallStartRecordingArgs,
  DesktopRecordingRecoveryState,
  DesktopRecordingSession,
  RecallPermissionKind,
  RecallPermissionStatus,
  ElectronAppInfo,
  RecallSdkStatePayload,
  ImportAppleNotesResult,
  GetAppleNoteContentResult,
  MenuActionEvent,
  DesktopShortcutEvent,
  DesktopRecallRealtimeEventPayload,
  type RecallMeetingEventPayload,
  OAuthAccessTokenGetRequestArgs,
  OAuthAccessTokenGetResponseArgs,
  OAuthAccessTokenSetArgs,
  WorkingContextMatch,
  WorkingContextAddSubscriptionArgs,
  WorkingContextRemoveSubscriptionArgs,
  WorkingContextSnapshot,
  WorkingContextStatus,
  StartDesktopAudioUploadArgs,
  StartDesktopAudioUploadResult,
  StartRecordingOutcome,
  FloatingAgentConfigureArgs,
  FloatingAgentDragMoveArgs,
  FloatingAgentEvent,
  FloatingAgentFirstMessageOnboardingCompletionArgs,
  FloatingAgentComposerMentionsArgs,
  FloatingAgentForegroundContextArgs,
  FloatingAgentNativeChatLimitUpsellArgs,
  FloatingAgentOpenMemAgentSettingsArgs,
  FloatingAgentOpenMentionArgs,
  FloatingAgentPresentationReleasedArgs,
  FloatingAgentResizeArgs,
  FloatingAgentSetPointerPassthroughArgs,
  FloatingAgentSetInteractionActiveArgs,
  FloatingAgentSetInteractionActiveResult,
  FloatingAgentSetHoldToTalkKeyArgs,
  FloatingAgentSetUnreadStateArgs,
  FloatingAgentTrackProactiveTrialBannerEventArgs,
  FloatingAgentVoiceCaptureFailedArgs,
  FloatingAgentWindowAnchor,
  FloatingAgentWindowDragMoveArgs,
} from "@/modules/electron/types";
import { TrackedEvent } from "@/domains/metrics";

/**
 * Should match the definition in our `mem-client-desktop` project.
 */
type ElectronAPI = {
  navigateToInternalUrl: (args: { url: string }) => Promise<void>;
  navigateToExternalUrl: (args: { url: string }) => Promise<void>;
  toggleMenu: () => Promise<void>;
  minimizeWindow: () => Promise<void>;
  maximizeWindow: () => Promise<void>;
  closeWindow: () => Promise<void>;
  // Passing tells the handler to keep checking for updates, even if there isn't one available yet.
  updateNow: (args: {
    restartBlockers: DesktopUpdateRestartBlocker[];
    waitForUpdate: boolean;
  }) => Promise<boolean>;
  desktopUpdater?: {
    getStatus: () => Promise<DesktopUpdateStatus>;
    check: () => Promise<DesktopUpdateStatus>;
    promptCriticalUpdate: (args: {
      source: DesktopCriticalUpdatePromptSource;
    }) => Promise<DesktopCriticalUpdatePromptResult>;
    install: (args: {
      isCritical: boolean;
      restartBlockers: DesktopUpdateRestartBlocker[];
    }) => Promise<DesktopUpdateInstallResult>;
    onStatusChanged: (cb: (status: DesktopUpdateStatus) => void) => () => void;
  };
  restartApp?: () => Promise<void>;
  openSystemSoundSettings?: (args?: { focus?: "input" | "output" }) => Promise<void>;
  setTheme: (args: { theme: ThemeType }) => Promise<void>;
  setCalendarNotificationEnabled: (args: { enabled: boolean }) => Promise<void>;
  getCalendarNotificationEnabled: () => Promise<{ enabled: boolean }>;
  setMeetingNotificationEnabled: (args: { enabled: boolean }) => Promise<void>;
  getMeetingNotificationEnabled: () => Promise<{ enabled: boolean }>;
  setSpellcheckEnabled: (args: { enabled: boolean }) => Promise<void>;
  getSpellcheckEnabled: () => Promise<{ enabled: boolean }>;
  setTeamsMeetingDetectionEnabled: (args: { enabled: boolean }) => Promise<void>;
  getTeamsMeetingDetectionEnabled: () => Promise<{ enabled: boolean }>;
  setAdvancedMeetingDetectionShown: (args: { shown: boolean }) => Promise<void>;
  getAdvancedMeetingDetectionShown: () => Promise<{ shown: boolean }>;
  setClientMetadata?: (args: { context: Record<string, unknown> }) => Promise<void>;
  clearClientMetadata?: () => Promise<void>;
  oauthAccessToken?: {
    set: (args: OAuthAccessTokenSetArgs) => Promise<void>;
    clear: () => Promise<void>;
    onGetRequested?: (cb: (args: OAuthAccessTokenGetRequestArgs) => void) => () => void;
    respondToGetRequest?: (args: OAuthAccessTokenGetResponseArgs) => Promise<void>;
    onRefreshRequested?: (cb: () => void) => () => void;
  };
  getElectronAppInfo?: () => Promise<ElectronAppInfo>;
  getMarketingAttribution?: () => Promise<DesktopMarketingAttribution | null>;
  onMarketingAttribution?: (
    cb: (attribution: DesktopMarketingAttribution) => void
  ) => Promise<() => void>;
  onBrowserMetadata?: (
    cb: (metadata: {
      segmentAnonymousId?: string;
      gaClientId?: string;
      gaSessionId?: string;
    }) => void
  ) => Promise<() => void>;
  setCalendarEvents?: (args: {
    ongoingEvents: CalendarEvent[];
    upcomingEvents: CalendarEvent[];
  }) => Promise<void>;
  onMenuActionEvent?: (cb: (payload: { actionEvent: MenuActionEvent }) => void) => () => void;
  onDesktopShortcutEvent?: (
    cb: (payload: { shortcutEvent: DesktopShortcutEvent }) => void
  ) => () => void;
  onTrackedEvent?: (cb: (payload: { trackedEvent: TrackedEvent }) => void) => () => void;
  setIsClientReady?: (args: { isReady: boolean }) => Promise<void>;
  importAppleNotes?: (options?: {
    collectAttachments?: boolean;
  }) => Promise<ImportAppleNotesResult>;
  getAppleNoteContent?: (noteId: number) => Promise<GetAppleNoteContentResult>;
  closeAppleNotesReader?: () => Promise<void>;
  resetTccAndClearCache?: (args: { appName: string }) => Promise<void>;
  /**
   * Events related to the Recall.ai Desktop SDK
   */
  recall: {
    prepareDesktopRecording: () => Promise<{ windowId: string }>;
    startRecording: (args: DesktopRecallStartRecordingArgs) => Promise<StartRecordingOutcome>;
    replaceRecording: (args: DesktopRecallReplaceRecordingArgs) => Promise<StartRecordingOutcome>;
    pause: (args: { windowId: string }) => Promise<void>;
    resume: (args: { windowId: string }) => Promise<void>;
    stop: (args: { windowId: string }) => Promise<void>;
    getRecordingSession: (
      args: DesktopAudioRecordingRef
    ) => Promise<DesktopRecordingSession | null>;
    getRecordingRecoveryState: (
      args: DesktopAudioRecordingRef
    ) => Promise<DesktopRecordingRecoveryState | null>;
    pauseRecordingSession: (args: DesktopAudioRecordingRef) => Promise<void>;
    resumeRecordingSession: (args: DesktopAudioRecordingRef) => Promise<void>;
    stopRecordingSession: (args: DesktopAudioRecordingRef) => Promise<void>;
    onEvent?: (cb: (payload: DesktopRecallRealtimeEventPayload) => void) => () => void;
    onMeetingEvent?: (cb: (payload: RecallMeetingEventPayload) => void) => () => void;
    requestPermission: (permission: RecallPermissionKind) => Promise<void>;
    getPermissionStatus: () => Promise<Record<RecallPermissionKind, RecallPermissionStatus>>;
    onPermissionStatus?: (
      cb: (payload: {
        event: { permission: RecallPermissionKind; status: RecallPermissionStatus };
      }) => void
    ) => () => void;
    openPermissionPane: (permission: RecallPermissionKind) => Promise<void>;
    getSdkState: () => Promise<RecallSdkStatePayload>;
    onSdkState?: (cb: (payload: RecallSdkStatePayload) => void) => () => void;
    testUnexpectedShutdown: () => Promise<void>;
  };
  audioArtifacts: {
    getAudioArtifactRef: (
      args: DesktopAudioRecordingRef
    ) => Promise<DesktopAudioArtifactRef | null>;
    getAudioArtifactSnapshot: (
      args: DesktopAudioRecordingRef
    ) => Promise<DesktopAudioArtifactSnapshot | null>;
    getAudioArtifactUploadFailureDebugInfo: (
      args: DesktopAudioRecordingRef
    ) => Promise<DesktopAudioArtifactUploadFailureDebugInfo | null>;
    finalizeAudioArtifact: (
      args: DesktopAudioRecordingRef
    ) => Promise<DesktopAudioArtifactRef | null>;
    removeAudioArtifact: (args: DesktopRemoveAudioArtifactArgs) => Promise<void>;
  };
  audioUpload: {
    startAudioUpload: (args: StartDesktopAudioUploadArgs) => Promise<StartDesktopAudioUploadResult>;
    onProgress?: (cb: (payload: DesktopAudioUploadProgressEvent) => void) => () => void;
    onCompleted?: (cb: (payload: DesktopAudioUploadCompletedEvent) => void) => () => void;
    onError?: (cb: (payload: DesktopAudioUploadErrorEvent) => void) => () => void;
  };
  audioWaveform: {
    onUpdate?: (cb: (payload: DesktopAudioWaveformUpdateEvent) => void) => () => void;
  };
  floatingAgent?: {
    configure?: (args: FloatingAgentConfigureArgs) => Promise<void>;
    setUnreadState?: (args: FloatingAgentSetUnreadStateArgs) => Promise<void>;
    setInteractionActive?: (
      args: FloatingAgentSetInteractionActiveArgs
    ) => Promise<FloatingAgentSetInteractionActiveResult>;
    presentationReleased?: (args: FloatingAgentPresentationReleasedArgs) => Promise<void>;
    setHoldToTalkKey?: (args: FloatingAgentSetHoldToTalkKeyArgs) => Promise<void>;
    startOnboardingNudge?: () => Promise<void>;
    completeFirstMessageOnboarding?: (
      args: FloatingAgentFirstMessageOnboardingCompletionArgs
    ) => Promise<void>;
    dragMove?: (args: FloatingAgentDragMoveArgs) => void;
    dragEnd?: (args: FloatingAgentDragMoveArgs) => Promise<{ anchor: FloatingAgentWindowAnchor }>;
    windowDragMove?: (args: FloatingAgentWindowDragMoveArgs) => void;
    windowDragEnd?: (args: FloatingAgentWindowDragMoveArgs) => Promise<void>;
    resize?: (args: FloatingAgentResizeArgs) => Promise<void>;
    setPointerPassthrough?: (args: FloatingAgentSetPointerPassthroughArgs) => void;
    getAnchor?: () => Promise<FloatingAgentWindowAnchor>;
    summonMainApp?: () => Promise<void>;
    openPanel?: () => Promise<void>;
    openNativeChatLimitUpsell?: (args: FloatingAgentNativeChatLimitUpsellArgs) => Promise<void>;
    openProactiveUpsell?: () => Promise<void>;
    hide?: () => Promise<void>;
    tooltipShow?: () => Promise<void>;
    tooltipHide?: () => Promise<void>;
    openTasks?: () => Promise<void>;
    onOpenTasks?: (cb: () => void) => () => void;
    openMention?: (args: FloatingAgentOpenMentionArgs) => Promise<void>;
    onOpenMention?: (cb: (args: FloatingAgentOpenMentionArgs) => void) => () => void;
    openMemAgentSettings?: (args: FloatingAgentOpenMemAgentSettingsArgs) => Promise<void>;
    onOpenMemAgentSettings?: (
      cb: (args: FloatingAgentOpenMemAgentSettingsArgs) => void
    ) => () => void;
    setForegroundContext?: (args: FloatingAgentForegroundContextArgs) => Promise<void>;
    getForegroundContext?: () => Promise<FloatingAgentForegroundContextArgs>;
    attachComposerMentions?: (args: FloatingAgentComposerMentionsArgs) => Promise<void>;
    takeComposerMentions?: () => Promise<FloatingAgentComposerMentionsArgs | null>;
    trackProactiveTrialBannerEvent?: (
      args: FloatingAgentTrackProactiveTrialBannerEventArgs
    ) => Promise<void>;
    rendererReady?: () => void;
    voiceCaptureFailed?: (args: FloatingAgentVoiceCaptureFailedArgs) => void;
    onEvent?: (cb: (event: FloatingAgentEvent) => void) => () => void;
  };
  workingContext?: {
    getStatus: () => Promise<WorkingContextStatus>;
    getSnapshot: () => Promise<WorkingContextSnapshot>;
    addSubscription: (args: WorkingContextAddSubscriptionArgs) => Promise<WorkingContextSnapshot>;
    removeSubscription: (
      args: WorkingContextRemoveSubscriptionArgs
    ) => Promise<WorkingContextSnapshot>;
    onMatch?: (cb: (payload: WorkingContextMatch) => void) => () => void;
  };
};

export const getElectronAPI = (): ElectronAPI | null => {
  const isDesktop = clientEnvModule.isDesktop();

  if (isDesktop) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any).electronAPI as ElectronAPI;
  }

  return null;
};
